import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link2, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { firecrawlApi } from "@/lib/api/firecrawl";
import type { IngestionDraft, RefreshHint, ScrapeOptions } from "./types";

interface LinkIngestionTabProps {
  onCapture: (draft: IngestionDraft) => void;
  onUpdateDraft: (updates: Partial<IngestionDraft>) => void;
}

const refreshHintLabels: Record<RefreshHint, string> = {
  static: "Static (rarely changes)",
  periodic: "Periodic (schedules, calendars)",
  manual: "Manual (admin triggers)",
  unknown: "Unknown",
};

const DEFAULT_SCRAPE_OPTIONS: ScrapeOptions = {
  formats: ['markdown'],
  onlyMainContent: true,
};

const LinkIngestionTab = ({ onCapture, onUpdateDraft }: LinkIngestionTabProps) => {
  const [url, setUrl] = useState("");
  const [refreshHint, setRefreshHint] = useState<RefreshHint>("unknown");
  const [isScraping, setIsScraping] = useState(false);

  const handleCapture = async () => {
    if (!url.trim()) return;

    // Basic URL validation
    let formattedUrl = url.trim();
    if (!formattedUrl.startsWith("http://") && !formattedUrl.startsWith("https://")) {
      formattedUrl = `https://${formattedUrl}`;
    }

    // Create draft immediately with processing status
    const draft: IngestionDraft = {
      source: {
        source_type: "link",
        url: formattedUrl,
        timestamp: new Date().toISOString(),
        refresh_hint: refreshHint,
      },
      raw_content: "",
      status: "processing",
      scrape_options: DEFAULT_SCRAPE_OPTIONS,
      data_type: null,
      structured_extraction: null,
      ai_notes: null,
    };

    onCapture(draft);
    setIsScraping(true);

    try {
      const response = await firecrawlApi.scrape(formattedUrl, DEFAULT_SCRAPE_OPTIONS);

      if (response.success && response.data) {
        onUpdateDraft({
          raw_content: response.data.markdown || "",
          raw_html: response.data.html,
          scraped_at: new Date().toISOString(),
          status: "processed",
          scrape_error: undefined,
        });
        toast.success("Page scraped successfully");
      } else {
        onUpdateDraft({
          status: "failed",
          scrape_error: response.error || "Unknown error occurred",
        });
        toast.error("Failed to scrape page", {
          description: response.error,
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to scrape";
      onUpdateDraft({
        status: "failed",
        scrape_error: errorMessage,
      });
      toast.error("Failed to scrape page", {
        description: errorMessage,
      });
    } finally {
      setIsScraping(false);
      setUrl("");
      setRefreshHint("unknown");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/activities"
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
          onKeyDown={(e) => e.key === "Enter" && !isScraping && handleCapture()}
          disabled={isScraping}
        />
        <Button
          onClick={handleCapture}
          disabled={!url.trim() || isScraping}
          className="bg-white/20 hover:bg-white/30 text-white shrink-0"
        >
          {isScraping ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Scraping...
            </>
          ) : (
            <>
              <Link2 className="w-4 h-4 mr-2" />
              Capture
            </>
          )}
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-white/60 text-xs shrink-0">Refresh:</span>
        <Select 
          value={refreshHint} 
          onValueChange={(v) => setRefreshHint(v as RefreshHint)}
          disabled={isScraping}
        >
          <SelectTrigger className="bg-white/10 border-white/20 text-white h-8 text-xs w-auto min-w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-white/20">
            {(Object.keys(refreshHintLabels) as RefreshHint[]).map((hint) => (
              <SelectItem
                key={hint}
                value={hint}
                className="text-white/80 focus:bg-white/10 focus:text-white text-xs"
              >
                {refreshHintLabels[hint]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default LinkIngestionTab;
